import { Tenant } from '../../types/properties';
import { User, Phone, Mail, Briefcase, Heart, Calendar, DollarSign, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../lib/format';
import { format } from 'date-fns';

interface TenantInfoProps {
  tenant: Tenant | null;
}

export function TenantInfo({ tenant }: TenantInfoProps) {
  if (!tenant) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center border-2 border-dashed border-[var(--t-border)] rounded-2xl">
        <User size={48} className="text-[var(--t-border-hover)] mb-4" />
        <h3 className="text-lg font-bold text-[var(--t-text)] mb-1">No Active Tenant</h3>
        <p className="text-sm text-[var(--t-text-muted)]">This property is currently vacant or no tenant has been assigned.</p>
        <button className="mt-6 px-4 py-2 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-lg hover:bg-[var(--t-primary-hover)] transition-colors">
          Add Tenant
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-4 p-4 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl">
        <div className="w-16 h-16 rounded-full bg-[var(--t-primary-dim)] flex items-center justify-center text-xl font-bold text-[var(--t-primary)] uppercase">
          {tenant.name.substring(0, 2)}
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">{tenant.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded bg-[var(--t-success-dim)] text-[var(--t-success)]">
              {tenant.status}
            </span>
            <span className="text-sm text-[var(--t-text-muted)]">Since {tenant.lease_start ? format(new Date(tenant.lease_start), 'MMM yyyy') : 'Unknown'}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl flex flex-col gap-4">
          <h3 className="text-sm font-bold text-[var(--t-text-muted)] uppercase tracking-wider">Contact Info</h3>

          {tenant.phone && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[var(--t-surface-hover)] flex items-center justify-center">
                <Phone size={14} className="text-[var(--t-text)]" />
              </div>
              <span className="text-sm text-[var(--t-text)] font-medium">{tenant.phone}</span>
            </div>
          )}

          {tenant.email && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[var(--t-surface-hover)] flex items-center justify-center">
                <Mail size={14} className="text-[var(--t-text)]" />
              </div>
              <span className="text-sm text-[var(--t-text)] font-medium">{tenant.email}</span>
            </div>
          )}

          {tenant.emergency_contact && (
            <div className="flex items-center gap-3 mt-2 p-3 bg-[var(--t-error)]/5 border border-[var(--t-error)]/20 rounded-xl">
              <AlertCircle size={14} className="text-[var(--t-error)] shrink-0" />
              <div className="flex flex-col">
                <span className="text-[10px] text-[var(--t-error)]/70 uppercase tracking-wider font-bold">Emergency Contact</span>
                <span className="text-sm text-[var(--t-text)] font-medium">{tenant.emergency_contact}</span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl flex flex-col gap-4">
          <h3 className="text-sm font-bold text-[var(--t-text-muted)] uppercase tracking-wider">Additional Details</h3>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[var(--t-surface-hover)] flex items-center justify-center">
              <Briefcase size={14} className="text-[var(--t-text)]" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-[var(--t-text-muted)] uppercase tracking-wider font-bold">Occupation</span>
              <span className="text-sm text-[var(--t-text)] font-medium">{tenant.occupation || 'Not specified'}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[var(--t-surface-hover)] flex items-center justify-center">
              <Heart size={14} className="text-[var(--t-text)]" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-[var(--t-text-muted)] uppercase tracking-wider font-bold">Pets</span>
              <span className="text-sm text-[var(--t-text)] font-medium">{tenant.pets || 'None'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
